import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiAxios from 'helpers/apiAxios';
import { handleAsyncThunkError } from 'redux/reduxHelpers';

const BASE = '/crm/dashboard/contracts';

export const fetchContractSummary = createAsyncThunk(
  'dashboardContracts/fetchSummary',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_summary_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch contract summary'));
    }
  }
);

export const fetchContractWeekly = createAsyncThunk(
  'dashboardContracts/fetchWeekly',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_weekly_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch weekly comparison'));
    }
  }
);

export const fetchContractMonthlyTrend = createAsyncThunk(
  'dashboardContracts/fetchMonthlyTrend',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_monthly_trend`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch monthly trend'));
    }
  }
);

export const fetchContractSalesUnit = createAsyncThunk(
  'dashboardContracts/fetchSalesUnit',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_sales_unit_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch sales unit stats'));
    }
  }
);

export const fetchContractDomesticOversea = createAsyncThunk(
  'dashboardContracts/fetchDomesticOversea',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_domestic_oversea_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch domestic/oversea stats'));
    }
  }
);

export const fetchContractTopRevenue = createAsyncThunk(
  'dashboardContracts/fetchTopRevenue',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_top_contracts`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch top contracts'));
    }
  }
);

export const fetchContractSalesIncharge = createAsyncThunk(
  'dashboardContracts/fetchSalesIncharge',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_sales_incharge_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch sales incharge stats'));
    }
  }
);

export const fetchContractProvince = createAsyncThunk(
  'dashboardContracts/fetchProvince',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_province_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch province stats'));
    }
  }
);

export const fetchContractDetailed = createAsyncThunk(
  'dashboardContracts/fetchDetailed',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_detailed_data`, { params });
      return res.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch detailed records'));
    }
  }
);

export const fetchContractFilterOptions = createAsyncThunk(
  'dashboardContracts/fetchFilterOptions',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/filter_options`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch filter options'));
    }
  }
);

export const fetchContractCountry = createAsyncThunk(
  'dashboardContracts/fetchCountry',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_country_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch country stats'));
    }
  }
);

export const fetchContractRegion = createAsyncThunk(
  'dashboardContracts/fetchRegion',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_region_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch region stats'));
    }
  }
);

export const fetchContractCategory = createAsyncThunk(
  'dashboardContracts/fetchCategory',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_category_data`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch category stats'));
    }
  }
);

// ── Initial state ──────────────────────────────────────────────────────────

const makeSection = (arrayDefault = false) => ({
  data:    arrayDefault ? [] : null,
  loading: false,
  error:   null
});

const initialState = {
  summary:         makeSection(),
  weekly:          makeSection(),
  monthlyTrend:    makeSection(true),
  salesUnit:       makeSection(true),
  domesticOversea: makeSection(true),
  topRevenue:      makeSection(),
  salesIncharge:   makeSection(true),
  province:        makeSection(true),
  country:         makeSection(true),
  region:          makeSection(true),
  category:        makeSection(true),
  detailed: {
    data:       [],
    pagination: { current_page: 1, per_page: 50, total_records: 0, total_pages: 0 },
    totals:     null,
    loading:    false,
    error:      null
  },
  filterOptions: makeSection(),
  lastUpdated:   null
};

// ── Slice ──────────────────────────────────────────────────────────────────

const thunkMap = [
  ['summary',         fetchContractSummary],
  ['weekly',          fetchContractWeekly],
  ['monthlyTrend',    fetchContractMonthlyTrend],
  ['salesUnit',       fetchContractSalesUnit],
  ['domesticOversea', fetchContractDomesticOversea],
  ['topRevenue',      fetchContractTopRevenue],
  ['salesIncharge',   fetchContractSalesIncharge],
  ['province',        fetchContractProvince],
  ['country',         fetchContractCountry],
  ['region',          fetchContractRegion],
  ['category',        fetchContractCategory],
  ['filterOptions',   fetchContractFilterOptions],
];

const dashboardContractsSlice = createSlice({
  name: 'dashboardContracts',
  initialState,
  reducers: {
    resetContractDashboard: () => initialState,
    resetError: (state) => {
      Object.keys(state).forEach((k) => {
        if (state[k] && typeof state[k] === 'object' && 'error' in state[k]) {
          state[k].error = null;
        }
      });
    }
  },
  extraReducers: (builder) => {
    thunkMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, (state) => {
          state[key].loading = true;
          state[key].error   = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state[key].data    = action.payload;
          state[key].loading = false;
          state.lastUpdated  = new Date().toISOString();
        })
        .addCase(thunk.rejected, (state, action) => {
          state[key].loading = false;
          state[key].error   = action.payload;
        });
    });

    builder
      .addCase(fetchContractDetailed.pending, (state) => {
        state.detailed.loading = true;
        state.detailed.error   = null;
      })
      .addCase(fetchContractDetailed.fulfilled, (state, action) => {
        state.detailed.data       = action.payload.data       || [];
        state.detailed.pagination = action.payload.pagination || initialState.detailed.pagination;
        state.detailed.totals     = action.payload.totals     || null;
        state.detailed.loading    = false;
        state.lastUpdated         = new Date().toISOString();
      })
      .addCase(fetchContractDetailed.rejected, (state, action) => {
        state.detailed.loading = false;
        state.detailed.error   = action.payload;
      });
  }
});

export const { resetContractDashboard, resetError } = dashboardContractsSlice.actions;
export default dashboardContractsSlice.reducer;
